import React, { FunctionComponent } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { CompletionBanner } from 'directory/listings/notification-modals/components/CompletionBanner';
import { DataTable } from 'directory/listings/notification-modals/components/DataTable';
import { NotificationBanner } from 'directory/listings/notification-modals/components/NotificationBanner';
import { ProgressStats } from 'directory/listings/notification-modals/components/UpdatingFlow';
import { TRANSLATION_NAMESPACE, COMPLETION_STATUS } from 'directory/listings/notification-modals/constants';
import { NotificationModalContentProps, CompletionStatus } from 'directory/listings/notification-modals/types';

import 'directory/listings/notification-modals/components/NotificationModalContent/NotificationModalContent.scss';

export const NotificationModalContent: FunctionComponent<NotificationModalContentProps> = ({
    notificationConfig,
    tableData = [],
    tableConfig,
    onRowSelect,
    showNotification = true,
    showTable = true,
    className = '',
    isUpdating = false,
    isCompleted = false,
    progress = 0,
    progressStats = {
        totalProfiles: 0,
        profilesUpdated: 0,
        profilesWithErrors: 0,
    },
    completionScenario = COMPLETION_STATUS.FULL_SUCCESS as CompletionStatus,
    field,
    onDownloadErrors,
}) => {
    const { t } = useTranslation(TRANSLATION_NAMESPACE);

    const renderHeaderContent = () => {
        if (isCompleted) {
            return (
                <>
                    <div
                        className={classNames('notification-modal-content__progress', {
                            'notification-modal-content__progress__completed': isCompleted,
                        })}
                    >
                        <ProgressStats
                            totalProfiles={progressStats.totalProfiles}
                            profilesUpdated={progressStats.profilesUpdated}
                            profilesWithErrors={progressStats.profilesWithErrors}
                            isCompleted
                        />
                    </div>

                    <CompletionBanner
                        scenario={completionScenario}
                        profilesUpdated={progressStats.profilesUpdated}
                        profilesWithErrors={progressStats.profilesWithErrors}
                        totalProfiles={progressStats.totalProfiles}
                        field={field}
                        onDownloadErrors={onDownloadErrors}
                    />
                </>
            );
        }

        if (isUpdating) {
            return (
                <>
                    <div
                        className={classNames('notification-modal-content__progress', {
                            'notification-modal-content__progress__updating': isUpdating && !isCompleted,
                        })}
                    >
                        <ProgressStats
                            totalProfiles={progressStats.totalProfiles}
                            profilesUpdated={progressStats.profilesUpdated}
                            profilesWithErrors={progressStats.profilesWithErrors}
                        />
                    </div>

                    <div className="notification-modal-content__progress-bar-container">
                        <div className="notification-modal-content__progress-bar">
                            <div
                                className="notification-modal-content__progress-fill"
                                style={{ width: `${progress}%` }}
                            >
                                <span className="notification-modal-content__progress-text">
                                    {Math.round(progress)}%
                                </span>
                            </div>
                        </div>
                    </div>
                </>
            );
        }

        if (showNotification) {
            return (
                <div className="notification-modal-content__banner">
                    <NotificationBanner {...notificationConfig} />
                </div>
            );
        }

        return null;
    };

    return (
        <div className={`notification-modal-content ${className}`.trim()}>
            {renderHeaderContent()}

            {showTable && tableData.length > 0 && (
                <div className="notification-modal-content__table">
                    <DataTable
                        data={tableData}
                        config={tableConfig}
                        onRowSelect={isUpdating ? undefined : onRowSelect}
                    />
                </div>
            )}

            {showTable && tableData.length === 0 && (
                <div className="notification-modal-content__empty">
                    <p className="text-muted text-center py-4">{t('NO_DATA_AVAILABLE')}</p>
                </div>
            )}
        </div>
    );
};
